<script setup lang="ts">
import { Comark } from '@comark/vue'

definePageMeta({ layout: 'text-content-page' })

const { locale, t } = useI18n({
  useScope: 'local'
})

useHead({
  title: t('title'),
  htmlAttrs: {
    lang: locale.value
  },
  meta: [
    {
      name: 'description',
      content: t('description')
    }
  ]
})

const md_de = `# Rohdaten Zugverspätungen

Dies enthält die nahezu Rohdaten von der DB Timetables-API. Wir teilen die Datenbank-Dumps, die intern als Backups verwendet werden. **Für die meisten Anwendungsfälle empfehlen wir, diesen Datensatz nicht zu verwenden, sondern stattdessen die [geparsten Zugverspätungen](/open-data/parsed-train-delays) zu nutzen.** Gute Anwendungsfälle für diesen Datensatz sind:

- Du brauchst etwas, das in den Rohdaten enthalten ist, aber in den geparsten Daten fehlt
- Du möchtest zu Bahn-Vorhersage beitragen und musst die Daten als Backup in die Datenbank laden

Wenn Du etwas benötigst, das noch nicht im geparsten Datensatz enthalten ist, denke bitte darüber nach unseren Parser zu erweitern, damit andere davon profitieren können. Wenn du das planst, kontaktiere uns am besten so früh wie möglich.

## Datenschema

Die gesammelten XML-Daten von der Timetables-API werden direkt in JSON konvertiert und dann in einer Datenbank gespeichert. Sie sind semantisch identisch mit dem XML. Die Schemadokumentation befindet sich in der Dokumentation der [Timetables-API](https://developers.deutschebahn.com/db-api-marketplace/apis/product/timetables){target="_blank"} selbst oder alternativ in unserer Implementierung im [Quellcode](https://gitlab.com/bahnvorhersage/bahnvorhersage/-/blob/master/api/iris.py){target="_blank"}.

## Datenformat

Die Timetables-API teilt Daten in Fahrpläne und Echtzeitdaten auf. Wir teilen die Fahrpläne und Echtzeitdaten separat als jährliche \`.tar\` Archive von Brotli-komprimierten täglichen \`.csv\` Dumps. Zudem enthalten die \`.tar\` Archive Hashes, die von unserem Backup-System verwendet werden. Die täglichen \`.csv\` Dateien enthalten die JSON-Payload und einige Metadaten. Das Schema wird in den folgenden Tabellen erklärt.

### Fahrplan

::caption-table{caption="Datenschema der Fahrplan Rohdaten."}
| Name | Beschreibung |
|------|--------------|
| \`stop_id\` | _Integer_: ID der Haltestelle (EVA-Nummer). Siehe [Haltestellen](/open-data/stops) |
| \`plan\` | _String_: JSON-Daten |
::

### Echtzeit

::caption-table{caption="Datenschema der Echtzeit Rohdaten."}
| Name | Beschreibung |
|------|--------------|
| \`stop_id\` | _Integer_: ID der Haltestelle (EVA-Nummer). Siehe [Haltestellen](/open-data/stops) |
| \`time_crawled\` | _ISO 8601 Timestamp_: Crawling-Zeitstempel. |
| \`change\` | _String_: JSON-Daten |
::

## Datenvollständigkeit

Der Datensatz ist aufgrund von Crawling-Fehlern unvollständig. Fehlende Daten werden im folgenden Data-Paper im Absatz "Completeness and Scope" beschrieben. Das Paper beschreibt zudem auch den Prozess der Datensammlung genau.

::btn-link{href="https://opus.bibliothek.uni-augsburg.de/opus4/127213"}
<i class="icon icon-box-arrow-up-right"></i> Preprint Data Paper
::

## Daten

Die Daten sind in der Mobilithek verfügbar: <small class="text-muted">Du musst eingeloggt sein, um Daten zu sehen und anzufordern.</small>

::btn-link{href="https://mobilithek.info/offers/938849406229053440"}
<i class="icon icon-box-arrow-up-right"></i> Fahrplan Rohdaten anfordern
::

::btn-link{href="https://mobilithek.info/offers/938859748233478144"}
<i class="icon icon-box-arrow-up-right"></i> Echtzeit Rohdaten anfordern
::

Der Datensatz enthält jährliche Archive. Neue Archive werden hochgeladen, nachdem das Jahr abgeschlossen ist.

## Lizenz

Die Daten sind unter der [Open Database License](https://opendatacommons.org/licenses/odbl/){target="_blank"} verfügbar. Credits müssen zugeordnet werden an: Bahn-Vorhersage, Deutsche Bahn

## Backup laden

Um die Rohdaten in die Datenbank zu laden, folge den Schritten auf [GitLab](https://gitlab.com/bahnvorhersage/bahnvorhersage).

_Wenn Du ein Problem mit dieser Dokumentation findest, dann öffne doch bitte ein Issue auf [GitLab](https://gitlab.com/bahnvorhersage/bahnvorhersage_frontend){target="_blank"}._
`

const md_en = `# Raw Train Delays

This contains the nearly raw crawled data from the DB Timetables API. We share the database dumps that are used internally as backups. **For most use cases, we recommend not using this dataset and instead using the [parsed train delays](/open-data/parsed-train-delays).** Good use cases for this dataset are:

- You need something contained in the raw data but missing from the parsed data
- You want to contribute to Bahn-Vorhersage and you need to load the data as backup into the database

If you need something not yet contained in the parsed dataset, please consider contributing to our parser so others can benefit. It's best to contact us early in the process.

## References

The collected XML data from the API is directly converted into JSON and then stored in a database. It is semantically identical to the XML. For reference, please consult the documentation of the [Timetables API](https://developers.deutschebahn.com/db-api-marketplace/apis/product/timetables){target="_blank"} itself or by looking at our implementation in the [source code](https://gitlab.com/bahnvorhersage/bahnvorhersage/-/blob/master/api/iris.py){target="_blank"}.

## Data Format

The Timetables API splits data into schedules and realtime data. We distribute the data as yearly \`.tar\` archives of Brotli-compressed daily \`.csv\` dumps. We also provide hashes used by our backup system. The daily \`.csv\` files contain the JSON payload and some metadata. The schema is explained in the following tables.

### Schedule

::caption-table{caption="Data schema of the RAW schedules."}
| Name | Description |
|------|-------------|
| \`stop_id\` | _Integer_: ID of the stop (EVA number). See [stops](/open-data/stops) |
| \`plan\` | _String_: JSON data |
::

### Realtime

::caption-table{caption="Data schema of the RAW realtime data."}
| Name | Description |
|------|-------------|
| \`stop_id\` | _Integer_: ID of the stop (EVA number). See [stops](/open-data/stops) |
| \`time_crawled\` | _ISO 8601 Timestamp_: Crawling timestamp. |
| \`change\` | _String_: JSON data |
::

## Data Completeness

The dataset is incomplete due to crawling failures. To avoid working with known incorrect or missing data, consult the completeness analysis in the paper describing the dataset.

<a href="https://opus.bibliothek.uni-augsburg.de/opus4/127213" target="_blank" rel="noopener noreferrer" class="btn btn-primary w-100 mb-3" role="button"><i class="icon icon-box-arrow-up-right"></i> Preprint Data Paper</a>

## Data

The data is available in the Mobilithek: <small class="text-muted">You have to be logged in to see and request data.</small>

::btn-link{href="https://mobilithek.info/offers/938849406229053440"}
<i class="icon icon-box-arrow-up-right"></i> Request raw schedules
::

::btn-link{href="https://mobilithek.info/offers/938859748233478144"}
<i class="icon icon-box-arrow-up-right"></i> Request raw realtime data
::

The dataset contains yearly archives. New archives are uploaded after the year is complete.

## License

The data is available under the [Open Database License](https://opendatacommons.org/licenses/odbl/){target="_blank"}. Credits must be attributed to: Bahn-Vorhersage, Deutsche Bahn

## Load Backup

In order to load the RAW data into the database, follow the steps on [GitLab](https://gitlab.com/bahnvorhersage/bahnvorhersage).

_If you find any issue with this documentation, please consider contributing on [GitLab](https://gitlab.com/bahnvorhersage/bahnvorhersage_frontend){target="_blank"}._
`

const md = computed(() => (locale.value === 'de' ? md_de : md_en))
</script>

<template>
  <LocaleSwitcher />
  <Suspense>
    <Comark :options="{ autoUnwrap: true }">{{ md }}</Comark>
  </Suspense>
</template>

<i18n lang="yaml">
de:
  title: Rohdaten Zugverspätungen
  description: Rohdaten Zugverspätungen, gesammelt von der Timetables-API. Schwierig zu verwenden.
en:
  title: Raw Delays
  description: Raw delay data collected from the Timetables API. Difficult to work with.
</i18n>
